import { fabric } from 'fabric_gesture';
import { iPoint, iPolygon } from '../model/geometry';

// Extending fabric Point class to make a new one with uid attribute
fabric.iPoint = iPoint;

// Extending fabric Polygon class to make a new one with uid attribute
fabric.iPolygon = iPolygon;

export class PolygonStore {
    private polys: Array<fabric.iPolygon>;
    public readonly _normalPolyOptions: PolygonOptions;
    public readonly _tempPolyOptions: PolygonOptions;

    constructor() {
        this._normalPolyOptions = {
            kind: 'annotation',
            selectable: false,
            objectCaching: false,
            hasControls: false,
            lockMovementX: true,
            lockMovementY: true,
            fill: 'transparent',
            stroke: 'white',
            strokeWidth: 2,
            borderScaleFactor: 0
        };
        this._tempPolyOptions = {
            ...this._normalPolyOptions,
            strokeDashArray: [5, 3]
        };
        this.polys = [];
    }

    setPointPos(uid: number, x: number, y: number) {
        // console.log('setting point', uid, x, y)
        for (let i = 0; i < this.polys.length; i++) {
            for (let j = 0; j < this.polys[i].points.length; j++) {
                if (this.polys[i].points[j].uid === uid) {
                    // console.log('old poly', this.polys[i], 'center', this.polys[i].getCenterPoint());
                    const oldPoly = this.polys[i];
                    oldPoly.points[j].x = x;
                    oldPoly.points[j].y = y;
                    this.polys[i] = new fabric.iPolygon(oldPoly.points, this._normalPolyOptions);
                    this.polys[i].label = oldPoly.label;
                    this.polys[i].id = oldPoly.id;
                    // console.log('new poly', this.polys[i], 'center', this.polys[i].getCenterPoint());
                    break;
                }
            }
        }
    }

    reduceOne({ uid }: fabric.iPoint) {
        let point = this.findPoint(uid);
        if (point.index > -1) {
            this.polys[point.parentIndex].points.splice(point.index, 1);
            return true;
        } else {
            console.warn('Cannot remove point with uid', uid);
            return false;
        }
    }

    last(): fabric.iPolygon {
        if (this.polys.length < 1)
            return console.warn('Getting last element of PolygonStore while it is empty', this);
        return this.polys[this.polys.length - 1];
    }

    get(index: number): fabric.iPolygon {
        if (index > this.polys.length)
            return console.warn('Getting polygons at index out of range', index, this.polys)
        return this.polys[index];
    }

    // Adds next point to polygon currently being drawn
    continue(p: fabric.iPoint, label?: string, id?:string) {
        const poly = new fabric.iPolygon([...this.polys.pop().points, p], this._tempPolyOptions);
        poly.label = label;
        poly.id = id;
        this.polys.push(poly);
    }

    findPoint(uid: number): ExtractedArrayElement<fabric.iPoint> {
        for (let i = 0; i < this.polys.length; i++) {
            for (let j = 0; j < this.polys[i].points.length; j++) {
                if (this.polys[i].points[j].uid === uid)
                    return {
                        element: this.polys[i].points[j],
                        index: j,
                        parentIndex: i
                    };
            }
        }
        return {
            element: undefined,
            index: -1,
            parentIndex: -1
        };
    }

    findPolyContainingPoint(uid: number) {
        for (let i = 0; i < this.polys.length; i++) {
            for (let j = 0; j < this.polys[i].points.length; j++) {
                if (this.polys[i].points[j].uid === uid)
                    return {
                        element: this.polys[i],
                        index: i
                    };
            }
        }
        return {
            element: undefined,
            index: -1
        };
    }

    /* temporary adds the polygon with different style when the polygon is
    still being drawn */
    add(polygon: Array<fabric.iPoint> | fabric.iPolygon, temporary?: boolean, label?: string, id ?: string) {
        if (polygon instanceof Array) {
            const poly = new fabric.iPolygon(polygon, temporary ? this._tempPolyOptions : this._normalPolyOptions);
            poly.label = label;
            poly.id = id;
            this.polys.push(poly);
            // console.log('added polygon as arr', polygon);
        } else {
            polygon.label = label;
            polygon.id = id;
            this.polys.push(polygon);
            // console.log('added polygon as obj', polygon);
        }
    }

    pop(): fabric.iPolygon {
        return this.polys.pop();
    }

    getIndexOf(polygon: fabric.iPolygon): number {
        return this.polys.indexOf(polygon);
    }

    all(): Array<fabric.iPolygon> { return this.polys; }

    setPolygonsSelectable(enable?: boolean) {
        this.polys.forEach(poly => {
            poly.selectable = enable ? true : false;
        });
    }
}

export interface PolygonOptions {
    kind: string;
    selectable?: boolean;
    objectCaching?: boolean;
    hasControls?: boolean;
    lockMovementX?: boolean;
    lockMovementY?: boolean;
    fill?: string;
    stroke?: string;
    strokeDashArray?: Array<number>;
    strokeWidth?: number;
    borderScaleFactor?: number;
}

export interface ExtractedArrayElement<T> {
    element: T;
    index: number;
    parentIndex?: number;
}


// WEBPACK FOOTER //
// ./src/app/model/polygon-store.ts